/* RESPONSIBLE TEAM: team-reporting */
import { indexBy } from 'underscore';
import moment from 'moment-timezone';
import {
  DayPosition,
  SchedulePreset,
  Step,
  type ExportRule,
} from 'embercom/models/reporting/dataset-export-schedule';
import type DatasetExportScheduleService from 'embercom/services/dataset-export-schedule-service';
import type IntlService from 'ember-intl/services/intl';
// @ts-ignore
import { copy } from 'ember-copy';

/**
 * Shared utilities for schedule frequency selection components.
 * Provides helper functions for managing export rule state, day selection,
 * interval configuration, and day position management.
 *
 * Used by:
 * - report-schedule-frequency-item
 * - export-schedule-frequency-item
 */
export class ScheduleFrequencyHelpers {
  // Maximum number of day names to display before truncating with ellipsis
  private static readonly MAX_VISIBLE_DAYS = 4;
  /**
   * Get the list of frequency options (weekly/monthly)
   */
  static getFrequencies(intl: IntlService, translationBasePath: string) {
    return [
      {
        text: intl.t(`${translationBasePath}.weeks`),
        value: Step.WEEKLY,
      },
      {
        text: intl.t(`${translationBasePath}.months`),
        value: Step.MONTHLY,
      },
    ];
  }

  /**
   * Get the list of days of the week with selection state
   */
  static getDaysOfWeek(intl: IntlService, localExportRule: ExportRule) {
    let days = [
      { text: intl.t('app.lib.reporting.week-days.sunday'), value: 0 },
      { text: intl.t('app.lib.reporting.week-days.monday'), value: 1 },
      { text: intl.t('app.lib.reporting.week-days.tuesday'), value: 2 },
      { text: intl.t('app.lib.reporting.week-days.wednesday'), value: 3 },
      { text: intl.t('app.lib.reporting.week-days.thursday'), value: 4 },
      { text: intl.t('app.lib.reporting.week-days.friday'), value: 5 },
      { text: intl.t('app.lib.reporting.week-days.saturday'), value: 6 },
    ];
    return days.map((day) => ({
      ...day,
      isSelected: localExportRule.daysOfWeek.includes(day.value),
    }));
  }

  /**
   * Update days of week selection with validation (filter out invalid days)
   */
  static updateDaysSelection(localExportRule: ExportRule, selectedDays: number[]): ExportRule {
    let validDays = selectedDays.filter((day) => day >= 0 && day <= 6);
    if (validDays.length > 0) {
      return {
        ...localExportRule,
        daysOfWeek: validDays,
      };
    }
    return localExportRule;
  }

  /**
   * Get the first weekday (Mon-Fri) from the selected days
   */
  static getFirstWeekDay(localExportRule: ExportRule): number | undefined {
    return localExportRule.daysOfWeek.find((day) => day > 0 && day < 6);
  }

  /**
   * Get the first weekday or fallback to first selected day (including weekend)
   */
  static getFirstWeekDayOrWeekend(localExportRule: ExportRule): number {
    let firstWeekDay = this.getFirstWeekDay(localExportRule);
    return firstWeekDay ?? localExportRule.daysOfWeek[0];
  }

  /**
   * Update step selection (weekly/monthly) with appropriate defaults
   * When switching to monthly: select single day and set day position
   * When switching to weekly: clear day position
   */
  static updateStepSelection(localExportRule: ExportRule, step: string): ExportRule {
    let dayPosition = localExportRule.interval.dayPosition;

    if (step === Step.MONTHLY) {
      return {
        ...localExportRule,
        daysOfWeek: [this.getFirstWeekDayOrWeekend(localExportRule)],
        interval: {
          ...localExportRule.interval,
          step,
          dayPosition: dayPosition ?? DayPosition.LAST,
        },
      };
    } else {
      return {
        ...localExportRule,
        interval: {
          ...localExportRule.interval,
          step,
          dayPosition: undefined,
        },
      };
    }
  }

  /**
   * Get a formatted label for selected days (truncates after MAX_VISIBLE_DAYS)
   */
  static getSelectedDaysLabel(intl: IntlService, localExportRule: ExportRule): string {
    let daysOfWeek = this.getDaysOfWeek(intl, localExportRule);
    let indexedDays = indexBy(daysOfWeek, 'value');
    let label = localExportRule.daysOfWeek.map((day) => indexedDays[day].text);

    if (label.length > this.MAX_VISIBLE_DAYS) {
      return `${label.slice(0, this.MAX_VISIBLE_DAYS).join(', ')}...`;
    }
    return label.join(', ');
  }

  /**
   * Update interval value with validation (minimum of 1)
   */
  static updateIntervalValue(localExportRule: ExportRule, value: string): ExportRule {
    let numericValue = Number(value);
    return {
      ...localExportRule,
      interval: {
        ...localExportRule.interval,
        value: value === '' ? 1 : Math.max(1, numericValue),
      },
    };
  }

  /**
   * Check if apply button should be disabled based on interval input
   */
  static isApplyButtonDisabled(intervalInputValue: string): boolean {
    if (!intervalInputValue || intervalInputValue.trim() === '') {
      return true;
    }
    let numericValue = Number(intervalInputValue);
    return isNaN(numericValue) || numericValue < 1;
  }

  /**
   * Create a default custom preset export rule (defaults to Every Monday)
   */
  static createDefaultCustomPreset(
    datasetExportScheduleService: DatasetExportScheduleService,
    timezone: string,
    existingDeliveryTime: ExportRule['deliveryTime'],
  ): ExportRule {
    let baseRule = datasetExportScheduleService.exportRuleFrom(
      SchedulePreset.EVERY_MONDAY,
      timezone,
    );
    return {
      ...baseRule,
      deliveryTime: { ...existingDeliveryTime },
    };
  }

  /**
   * Initialize local export rule from args
   * If isPreset: create default custom preset
   * Otherwise: deep copy existing rule
   */
  static initializeLocalExportRule(
    exportRule: ExportRule,
    isPreset: boolean,
    datasetExportScheduleService: DatasetExportScheduleService,
    timezone: string,
  ): ExportRule {
    if (isPreset) {
      return this.createDefaultCustomPreset(
        datasetExportScheduleService,
        timezone,
        exportRule.deliveryTime,
      );
    } else {
      return copy(exportRule, true);
    }
  }

  /**
   * Update day position in the export rule
   */
  static updateDayPosition(localExportRule: ExportRule, dayPosition: DayPosition): ExportRule {
    return {
      ...localExportRule,
      interval: {
        ...localExportRule.interval,
        dayPosition,
      },
    };
  }

  /**
   * Get day position items with selection state
   */
  static getDayPositionItems(
    intl: IntlService,
    translationBasePath: string,
    localExportRule: ExportRule,
  ) {
    let items = [
      {
        text: intl.t(`${translationBasePath}.first`),
        value: DayPosition.FIRST,
      },
      {
        text: intl.t(`${translationBasePath}.second`),
        value: DayPosition.SECOND,
      },
      {
        text: intl.t(`${translationBasePath}.third`),
        value: DayPosition.THIRD,
      },
      {
        text: intl.t(`${translationBasePath}.last`),
        value: DayPosition.LAST,
      },
    ];
    return items.map((item) => ({
      ...item,
      isSelected: localExportRule.interval.dayPosition === item.value,
    }));
  }

  /**
   * Get the selected day position label with fallback to FIRST
   */
  static getSelectedDayPositionLabel(
    intl: IntlService,
    translationBasePath: string,
    localExportRule: ExportRule,
  ): string {
    let dayPositionItems = this.getDayPositionItems(intl, translationBasePath, localExportRule);
    let indexedItems = indexBy(dayPositionItems, 'value');

    if (localExportRule.interval.dayPosition) {
      return indexedItems[localExportRule.interval.dayPosition].text;
    }
    return indexedItems[DayPosition.FIRST].text;
  }

  /**
   * Get preset frequency items for dropdown (Daily, Weekly on Monday, etc.)
   */
  static getFrequencyPresetItems(
    intl: IntlService,
    translationBasePath: string,
    exportRule: ExportRule,
    timezone: string,
    datasetExportScheduleService: DatasetExportScheduleService,
    onSelectCallback: (preset: SchedulePreset) => void,
  ) {
    let oneMinuteFromNow = this.getOneMinuteFromNowLabel(timezone);
    let items = [
      {
        text: intl.t('reporting.schedule-delivery.frequency-dropdown.one-minute', {
          time: oneMinuteFromNow,
        }),
        value: SchedulePreset.ONE_MINUTE,
        onSelectItem: () => onSelectCallback(SchedulePreset.ONE_MINUTE),
        isSelected: datasetExportScheduleService.isPresetRule(
          exportRule,
          SchedulePreset.ONE_MINUTE,
          timezone,
        ),
      },
      {
        text: intl.t(`${translationBasePath}.frequency-dropdown.daily`),
        value: SchedulePreset.DAILY,
        onSelectItem: () => onSelectCallback(SchedulePreset.DAILY),
        isSelected: datasetExportScheduleService.isPresetRule(
          exportRule,
          SchedulePreset.DAILY,
          timezone,
        ),
      },
      {
        text: intl.t(`${translationBasePath}.frequency-dropdown.every-monday`),
        value: SchedulePreset.EVERY_MONDAY,
        onSelectItem: () => onSelectCallback(SchedulePreset.EVERY_MONDAY),
        isSelected: datasetExportScheduleService.isPresetRule(
          exportRule,
          SchedulePreset.EVERY_MONDAY,
          timezone,
        ),
      },
      {
        text: intl.t(`${translationBasePath}.frequency-dropdown.every-friday`),
        value: SchedulePreset.EVERY_FRIDAY,
        onSelectItem: () => onSelectCallback(SchedulePreset.EVERY_FRIDAY),
        isSelected: datasetExportScheduleService.isPresetRule(
          exportRule,
          SchedulePreset.EVERY_FRIDAY,
          timezone,
        ),
      },
      {
        text: intl.t(`${translationBasePath}.frequency-dropdown.first-day-of-month`),
        value: SchedulePreset.FIRST_DAY_OF_MONTH,
        onSelectItem: () => onSelectCallback(SchedulePreset.FIRST_DAY_OF_MONTH),
        isSelected: datasetExportScheduleService.isPresetRule(
          exportRule,
          SchedulePreset.FIRST_DAY_OF_MONTH,
          timezone,
        ),
      },
      {
        text: intl.t(`${translationBasePath}.frequency-dropdown.last-day-of-month`),
        value: SchedulePreset.LAST_DAY_OF_MONTH,
        onSelectItem: () => onSelectCallback(SchedulePreset.LAST_DAY_OF_MONTH),
        isSelected: datasetExportScheduleService.isPresetRule(
          exportRule,
          SchedulePreset.LAST_DAY_OF_MONTH,
          timezone,
        ),
      },
    ];

    let enableOneMinuteFrequency =
      localStorage.getItem('enable-schedule-delivery-one-minute') === 'true';

    if (!enableOneMinuteFrequency) {
      items = items.filter((item) => item.value !== SchedulePreset.ONE_MINUTE);
    }

    return items;
  }

  static getOneMinuteFromNowLabel(timezone: string): string {
    let now = moment.tz(timezone);
    let oneMinuteLater = now.clone().add(1, 'minute');
    return oneMinuteLater.format('h:mmA');
  }

  /**
   * Check if custom frequency is selected (no preset matches)
   */
  static isCustomFrequencySelected(frequencyItems: Array<{ isSelected: boolean }>): boolean {
    return !frequencyItems.some((item) => item.isSelected);
  }

  /**
   * Get hours array for time selection dropdown (0-23 with formatted labels)
   */
  static getHours(timezone: string, momentLib: any) {
    return Array.from(Array(24), (_, i) => ({
      text: `${momentLib({ hour: i }).format('h:mmA')} ${timezone}`,
      value: i,
    }));
  }
}
